import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import StockService from '../../Api_Module/Api_Services/StockService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { TrendingUp } from 'lucide-react';
import { useTheme } from '../ThemeContext';

interface RatingsHistoryData {
  date: string;
  analystRatingsStrongBuy: number;
  analystRatingsBuy: number;
  analystRatingsHold: number;
  analystRatingsSell: number;
  analystRatingsStrongSell: number;
}

const AnalystRatingsHistory: React.FC = () => {
  const { symbol: routeSymbol } = useParams<{ symbol?: string }>();
  const reduxSymbol = useSelector((state: any) => state.app?.currentSymbol);
  const symbol = reduxSymbol || routeSymbol || '';
  const { theme } = useTheme();

  const tickColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.6)' : '#111827';
  const axisGridColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)';
  const labelFill = theme === 'dark' ? '#cbd5e1' : '#111827';

  const [ratingsHistory, setRatingsHistory] = useState<RatingsHistoryData[]>([]);
  const [isSmall, setIsSmall] = useState<boolean>(false);
  const chartAreaRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onResize = () => setIsSmall(typeof window !== 'undefined' ? window.innerWidth < 640 : false);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      if (!symbol) return;
      setLoading(true);
      setError(null);
      try {
        const data = await StockService.getAnalystRatingsHistory(symbol);
        if (!cancelled) {
          setRatingsHistory(data);
        }
      } catch (e: any) {
        if (!cancelled) {
          console.error(e);
          setError(e?.message || 'Unable to load ratings history');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [symbol]);

  const formatXAxis = (tickItem: string) => {
    const date = new Date(tickItem);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const dataWithTotal = ratingsHistory.map(d => ({
    ...d,
    total:
      (d.analystRatingsStrongBuy || 0) +
      (d.analystRatingsBuy || 0) +
      (d.analystRatingsHold || 0) +
      (d.analystRatingsSell || 0) +
      (d.analystRatingsStrongSell || 0),
  }));

  return (
    <div className="bg-theme-secondary/30 rounded-lg border border-theme-tertiary/30 p-4 h-[350px] flex flex-col">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h2 className="text-base font-bold text-theme-primary flex items-center gap-2">
            <TrendingUp className="text-cyan-400" size={20} />
            Analyst Ratings History
          </h2>
          <p className="text-xs text-theme-secondary mt-1">
            Monthly trend of analyst ratings for {symbol}.
          </p>
        </div>
        <a
          href="https://stockfortress.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] select-none transition-colors whitespace-nowrap"
          style={{ color: 'rgba(255, 255, 255, 0.4)' }}
        >
          Powered by StockFortress
        </a>
      </div>
      <div
        className="flex-1"
        style={{ width: '100%', touchAction: isSmall ? 'none' as any : 'pan-y' as any }}
        ref={chartAreaRef}
        onTouchStart={(e) => {
          if (!isSmall) return;
          const t = e.touches[0] || e.changedTouches[0];
          const target = (t && document.elementFromPoint(t.clientX, t.clientY)) || chartAreaRef.current?.querySelector('svg.recharts-surface') || chartAreaRef.current?.querySelector('.recharts-wrapper');
          if (t && target) {
            if ('PointerEvent' in window) {
              (target as Element).dispatchEvent(new PointerEvent('pointerover', { bubbles: true, clientX: t.clientX, clientY: t.clientY } as any));
              (target as Element).dispatchEvent(new PointerEvent('pointermove', { bubbles: true, clientX: t.clientX, clientY: t.clientY } as any));
            } else {
              (target as Element).dispatchEvent(new MouseEvent('mouseover', { bubbles: true, clientX: t.clientX, clientY: t.clientY } as any));
              (target as Element).dispatchEvent(new MouseEvent('mousemove', { bubbles: true, clientX: t.clientX, clientY: t.clientY } as any));
            }
          }
        }}
        onTouchMove={(e) => {
          if (!isSmall) return;
          const t = e.touches[0] || e.changedTouches[0];
          const target = (t && document.elementFromPoint(t.clientX, t.clientY)) || chartAreaRef.current?.querySelector('svg.recharts-surface') || chartAreaRef.current?.querySelector('.recharts-wrapper');
          if (t && target) {
            if ('PointerEvent' in window) {
              (target as Element).dispatchEvent(new PointerEvent('pointermove', { bubbles: true, clientX: t.clientX, clientY: t.clientY } as any));
            } else {
              (target as Element).dispatchEvent(new MouseEvent('mousemove', { bubbles: true, clientX: t.clientX, clientY: t.clientY } as any));
            }
          }
        }}
        onTouchEnd={(e) => {
          if (!isSmall) return;
          const t = e.changedTouches[0];
          const target = (t && document.elementFromPoint(t.clientX, t.clientY)) || chartAreaRef.current?.querySelector('svg.recharts-surface') || chartAreaRef.current?.querySelector('.recharts-wrapper');
          if (t && target) {
            if ('PointerEvent' in window) {
              (target as Element).dispatchEvent(new PointerEvent('pointerleave', { bubbles: true, clientX: t.clientX, clientY: t.clientY } as any));
            } else {
              (target as Element).dispatchEvent(new MouseEvent('mouseleave', { bubbles: true, clientX: t.clientX, clientY: t.clientY } as any));
            }
          }
        }}
      >
        <ResponsiveContainer>
          <BarChart
            data={dataWithTotal}
            margin={{ top: 20, right: 0, left: isSmall ? -10 : -20, bottom: 0 }}
            barGap={4}
            barCategoryGap="20%"
          >
            <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={axisGridColor} />
            <XAxis
              dataKey="date"
              tickFormatter={formatXAxis}
              stroke={tickColor}
              fontSize={isSmall ? 10 : 12}
              interval={isSmall ? 'preserveStartEnd' : 0}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              stroke={tickColor}
              fontSize={isSmall ? 10 : 12}
              tickLine={false}
              axisLine={false}
              tickCount={isSmall ? 5 : 6}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'rgba(20, 20, 30, 0.9)',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                color: '#fff',
                borderRadius: '0.5rem',
                fontSize: '0.875rem'
              }}
              labelStyle={{ color: '#fff', fontWeight: 'bold' }}
              cursor={{ fill: 'rgba(255, 255, 255, 0.1)' }}
              isAnimationActive={false}
            />
            <Legend
              verticalAlign="top"
              align="right"
              iconSize={10}
              wrapperStyle={{ top: -4, right: 0, color: theme === 'dark' ? '#fff' : '#111827', fontSize: '12px' }}
            />
            <Bar dataKey="analystRatingsStrongBuy" stackId="a" name="Strong Buy" fill="#10B981" radius={[4, 4, 0, 0]} />
            <Bar dataKey="analystRatingsBuy" stackId="a" name="Buy" fill="#34D399" radius={[4, 4, 0, 0]} />
            <Bar dataKey="analystRatingsHold" stackId="a" name="Hold" fill="#FBBF24" radius={[4, 4, 0, 0]} />
            <Bar dataKey="analystRatingsSell" stackId="a" name="Sell" fill="#F87171" radius={[4, 4, 0, 0]} />
            <Bar dataKey="analystRatingsStrongSell" stackId="a" name="Strong Sell" fill="#EF4444" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AnalystRatingsHistory;
